import {
  Box,
  ClickAwayListener,
  FormHelperText,
  Menu,
  MenuItem,
  Paper,
  Popper,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  AppliedTaxes,
  DefaultTax,
  Descriptions,
  initialServiceData,
  ServiceData,
  serviceValues as initialServiceValues,
  ServiceValues,
  typeTaxEnum,
} from "../../../../types/budget/service-item";
import { ExchangeData } from "../../../../types/budget/budget";
import { LabeledInput } from "../../../UI/LabeledInput";
import { ServiceOptions } from "../../../../utils/serviceTypesFromBudget";
import { useProject } from "../../../../contexts/project.context";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { BsThreeDotsVertical } from "react-icons/bs";
import { IoIosCloseCircle } from "react-icons/io";
import { MdCardGiftcard, MdExpandLess, MdExpandMore } from "react-icons/md";
import { formatUnitValue } from "../../../../utils/unitValue";
import { TabActive } from "../../../../types";
import { ReactComponent as DescriptionIcon } from "../../../../assets/styled-icons/Description.svg";
import { ReactComponent as ApplicationPlaceIcon } from "../../../../assets/styled-icons/ApplicationPlace.svg";
import { ReactComponent as PercentIcon } from "../../../../assets/styled-icons/budget/material-symbols_percent.svg";
import DescriptionModal from "../../../Project/components/DescriptionModal";
import ApplicationPlaceModal from "../../../Project/components/ApplicationPlaceModal";
import TaxesModal from "../../../Project/components/TaxesModal";
import { FiCheckSquare, FiUserCheck } from "react-icons/fi";
import { LabeledInputFull } from "../../../UI/LabeledInputFull";
import { hasCustomTaxes } from "../../../../utils/utils";
import { MdWarning } from "react-icons/md";
import UnitValueInput from "../../UnitValueInput";
import { formatCurrencyValue } from "../../../../utils/currencySymbol";
import { useServices } from "../../../../contexts/ServicesContext/projectservices.context";
import {
  daysBetweenInclusive,
  formatBRDate,
  parseBRDate,
  applyDateMask,
  startOfDayTs,
} from "../../Budget/ServiceItem";
import { DateCalendar, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

type Props = {
  title: string;
  serviceData: ServiceData;
  type: string;
  index: number;
  currency: string;
  categoryExchange?: ExchangeData;
  descriptionFocus: any;
  setDescriptionFocus: any;
  serviceFocus: any;
  setServiceFocus: any;
  mainAppliedTaxes?: AppliedTaxes;
};

export const FREE_SERVICE_FIELD = [
  "EQUIPMENTS",
  "ATTRACTION",
  "BRINDES_INCENTIVOS_PREMIACOES",
  "CENOGRAPHY_DECORATION",
  "COMMUNICATIONS",
  "CONTENT",
  "FOTO_VIDEO",
  "LICENCES_TAXES",
  "GRAPHIC_MATERIAL",
  "MOUNTING_COMPANY",
  "NECESSARY_ADDITIONS",
  "HUMAN_RESOURCES_STAFF",
  "EVENT_INSURANCE",
  "TECHNOLOGY",
  "TRAVEL_INSURANCE",
  "SERVICE_SUPPORT",
];

export function useTextareaOverflow(
  inputRef: React.RefObject<HTMLTextAreaElement>,
  value: string | undefined
) {
  const [hasOverflow, setHasOverflow] = useState(false);

  const recompute = () => {
    const el = inputRef.current;
    if (!el) return;

    const cs = window.getComputedStyle(el);
    const lineHeight = parseFloat(cs.lineHeight || "0");
    if (!lineHeight) {
      setHasOverflow(false);
      return;
    }
    const paddingY =
      parseFloat(cs.paddingTop || "0") + parseFloat(cs.paddingBottom || "0");
    const contentHeight = el.scrollHeight - paddingY;
    const lines = contentHeight / lineHeight;

    setHasOverflow(lines > 1.05);
  };

  useLayoutEffect(recompute, [inputRef, value]);
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    const ro = new ResizeObserver(recompute);
    ro.observe(el);
    return () => ro.disconnect();
  }, [inputRef]);

  return hasOverflow;
}

const ServiceGroupItem: React.FC<Props> = ({
  serviceData,
  type,
  currency,
  categoryExchange,
  descriptionFocus,
  setDescriptionFocus,
  serviceFocus,
  setServiceFocus,
  mainAppliedTaxes,
}: Props) => {
  const { activeServiceDispatch, activeService, updateService } = useServices();

  const { tabActive, event } = useProject();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [openServiceMenu, setOpenServiceMenu] = useState(false);
  const [descriptionModal, setDescriptionModal] = useState(false);
  const [applicationPlaceModal, setApplicationPlaceModal] = useState(false);
  const [taxesModal, setTaxesModal] = useState(false);
  const [serviceValues, setServiceValues] = useState<ServiceValues>(
    JSON.parse(JSON.stringify(initialServiceValues))
  );
  const isFreeService = FREE_SERVICE_FIELD?.includes(type);
  const inputRef = useRef<any>(null);
  const serviceInputRef = useRef<any>(null);
  const hasOverflow = useTextareaOverflow(inputRef, serviceValues?.observation);
  const serviceHasOverflow = useTextareaOverflow(
    serviceInputRef,
    serviceValues?.serviceType
  );
  const isInputExpanded = descriptionFocus === serviceData?._id;
  const isInputServiceExpanded = serviceFocus === serviceData?._id;

  // Refs para flush de estado pendente no unmount (evita perda de dados ao colapsar accordion)
  const serviceValuesRef = useRef<ServiceValues>(serviceValues);
  const serviceDataRef = useRef(serviceData);
  serviceValuesRef.current = serviceValues;
  serviceDataRef.current = serviceData;

  useEffect(() => {
    return () => {
      // Ao desmontar, persiste qualquer alteração local pendente no contexto
      const currentValues = serviceValuesRef.current;
      const currentServiceData = serviceDataRef.current;
      if (!currentServiceData?._id) return;

      const updatedServiceData = { ...currentServiceData };
      updatedServiceData.budgetValues = { ...currentValues };
      activeServiceDispatch({
        type: "UPDATE_CALC_VALUES",
        payload: updatedServiceData,
        versionInfo: { version: 0, comparisonVersion: 0, status: "OPEN" },
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [dailyAnchorEl, setDailyAnchorEl] = useState<HTMLElement | null>(null);
  const [openDailyMenu, setOpenDailyMenu] = useState(false);

  const [checkInAnchorEl, setCheckInAnchorEl] = useState<HTMLElement | null>(
    null
  );
  const [openCheckInCalendar, setOpenCheckInCalendar] = useState(false);
  const [checkInDate, setCheckInDate] = useState<Date | null>(null);
  const [checkInText, setCheckInText] = useState("");

  const [checkOutAnchorEl, setCheckOutAnchorEl] = useState<HTMLElement | null>(
    null
  );
  const [openCheckOutCalendar, setOpenCheckOutCalendar] = useState(false);
  const [checkOutDate, setCheckOutDate] = useState<Date | null>(null);
  const [checkOutText, setCheckOutText] = useState("");
  const dailyPopperPaperRef = useRef<HTMLDivElement | null>(null);

  const verifyDates = (inDate: Date | null, outDate: Date | null) => {
    if (inDate && outDate) {
      const days = daysBetweenInclusive(inDate, outDate, type);
      if (days !== serviceValues.daily) handleInputBlur(days || 1, "daily");
    }
  };

  const canSetCheckIn = (candidate: Date, currentCheckOut: Date | null) => {
    if (!currentCheckOut) return true;
    const cand = startOfDayTs(candidate);
    const out = startOfDayTs(currentCheckOut);
    if (cand > out) {
      toast.warn("Check-in não pode ser após o Check-out.");
      return false;
    }
    return true;
  };

  const canSetCheckOut = (candidate: Date, currentCheckIn: Date | null) => {
    if (!currentCheckIn) return true;
    const cand = startOfDayTs(candidate);
    const inc = startOfDayTs(currentCheckIn);
    if (cand < inc) {
      toast.warn("Check-out não pode ser antes do Check-in.");
      return false;
    }
    return true;
  };

  const { compareServiceValues, serviceValuesKey, stepServiceValues } =
    useMemo(() => {
      let stepServiceValues = JSON.parse(
        JSON.stringify(serviceData.budgetValues)
      );
      let compareServiceValues = {} as ServiceValues;
      let key: keyof typeof serviceData = "budgetValues";

      return {
        compareServiceValues,
        serviceValuesKey: key,
        stepServiceValues,
      };
    }, [tabActive, serviceData]);

  // Sincroniza serviceValues com serviceData quando ele muda (ex: após recálculo)
  // Preserva campos editáveis localmente (observation, serviceType, descriptions, etc.)
  // e atualiza apenas campos calculados (taxData, totals)
  const isFirstMount = useRef(true);
  useEffect(() => {
    const contextValues = JSON.parse(JSON.stringify(serviceData.budgetValues));
    if (isFirstMount.current) {
      // Na primeira montagem, inicializa tudo do contexto
      isFirstMount.current = false;
      setServiceValues(contextValues);
    } else {
      // Após recálculos, preserva campos editáveis locais e atualiza apenas campos calculados
      setServiceValues((prev) => {
        return {
          ...prev,
          taxData: contextValues.taxData,
          totals: contextValues.totals,
          appliedTaxes: contextValues.appliedTaxes,
        };
      });
    }
  }, [serviceData.budgetValues]);

  // Função que atualiza o estado local e dispara o recálculo
  function handleFieldChange(
    value: any,
    field: keyof typeof serviceData.budgetValues
  ) {
    // Atualiza o estado local para feedback visual imediato
    let newState = JSON.parse(JSON.stringify(serviceValues));
    if (field === "taxData") {
      newState.taxData.tariff.value = value;
    } else {
      newState = {
        ...newState,
        [field]: value,
      };
    }
    setServiceValues(newState);

    // Dispara o recálculo usando serviceData como base, mas com budgetValues do estado local
    const updatedServiceData = JSON.parse(JSON.stringify(serviceData));
    updatedServiceData[serviceValuesKey] = { ...newState };
    activeServiceDispatch({
      type: "UPDATE_CALC_VALUES",
      payload: updatedServiceData,
      versionInfo: { version: 0, comparisonVersion: 0, status: "OPEN" },
    });
  }

  function handleStateChange(
    value: any,
    field: keyof typeof serviceData.budgetValues
  ) {
    let newState = JSON.parse(JSON.stringify(serviceValues));
    if (field === "taxData") {
      newState.taxData.tariff.value = value;
    } else {
      newState = {
        ...newState,
        [field]: value,
      };
    }
    setServiceValues(newState);
  }

  function handleInputBlur(
    value: any,
    field: keyof typeof serviceData.budgetValues
  ) {
    const newState = { ...serviceValues, [field]: value };
    const updatedServiceData = { ...serviceData };
    updatedServiceData[serviceValuesKey] = { ...newState };
    activeServiceDispatch({
      type: "UPDATE_CALC_VALUES",
      payload: updatedServiceData,
      versionInfo: { version: 0, comparisonVersion: 0, status: "OPEN" },
    });
    setServiceValues(newState);
  }

  function handleChangeDailyManually(
    value: any,
    field: keyof typeof serviceData.budgetValues
  ) {
    // if (field === "quantity" && value <= 0) {
    //   return toast.warn("Quantidade dever ser igual ou maior que 1");
    // }

    const newState = {
      ...serviceValues,
      [field]: value,
      inputDate: "",
      outDate: "",
    };

    const updatedServiceData = { ...serviceData };
    updatedServiceData[serviceValuesKey] = { ...newState };

    activeServiceDispatch({
      type: "UPDATE_CALC_VALUES",
      payload: updatedServiceData,
    });
    setServiceValues(newState);
    setCheckOutDate(null);
    setCheckInDate(null);
    setCheckInText("");
    setCheckOutText("");
  }

  function handleDescriptionChange(descriptions: Descriptions) {
    let newState = { ...serviceValues };
    newState = {
      ...newState,
      descriptions: descriptions,
    };
    setServiceValues(newState);
  }

  function handleDeleteService(): void {
    Swal.fire({
      title: "Tem certeza?",
      text: "Você está prestes a apagar um serviço",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      cancelButtonText: "Não, cancelar",
      confirmButtonText: "Sim, prossiga",
    }).then((result) => {
      if (result.isConfirmed) {
        const indexDeleted = activeService.budgetServices.findIndex(
          (el) => el._id === serviceData._id
        );
        activeService.budgetServices = activeService.budgetServices.filter(
          (el, index) => index !== indexDeleted
        );
        if (activeService._id)
          updateService(activeService._id, activeService, "SAVE", -1);
        activeServiceDispatch({
          type: "DELETE_SERVICE",
          payload: serviceData._id,
        });
        Swal.fire({
          title: "Apagado",
          text: "O serviço foi apagado",
          icon: "success",
        });
      }
    });
  }

  function submitForm(valuesOverride?: Partial<ServiceValues>) {
    const updatedServiceData = { ...serviceData };
    const valuesToSubmit = {
      ...serviceValues,
      ...valuesOverride,
    };

    if (valuesOverride) {
      setServiceValues(valuesToSubmit);
    }

    updatedServiceData[serviceValuesKey] = { ...valuesToSubmit };

    const servicesList = activeService.budgetServices.map((el) => {
      if (el._id === updatedServiceData._id) {
        return updatedServiceData;
      } else return el;
    });
    activeService.budgetServices = servicesList;
    if (activeService._id)
      updateService(activeService?._id, activeService, "SAVE", -1);
    activeServiceDispatch({
      type: "UPDATE_SERVICE",
      payload: updatedServiceData,
      versionInfo: { version: 0, comparisonVersion: 0, status: "OPEN" },
    });
  }

  const isCortesy =
    serviceValues?.taxData?.tariff.typeTax === typeTaxEnum.CORTESY;
  const isIncluded =
    serviceValues?.taxData?.tariff.typeTax === typeTaxEnum.INCLUDED;
  const isAcClient =
    serviceValues?.taxData?.tariff.typeTax === typeTaxEnum.AC_CLIENT;

  const handleUpdateUnitValue = (field: keyof DefaultTax, value: any) => {
    // Usar estado local como base para preservar alterações pendentes (ex: descrição)
    const updatedServiceData = JSON.parse(JSON.stringify(serviceData));
    const localValues = JSON.parse(JSON.stringify(serviceValues));

    // Atualizar o campo específico no tariff
    if (localValues?.taxData?.tariff) {
      localValues.taxData.tariff[field] = value;
    }

    updatedServiceData[serviceValuesKey] = { ...localValues };

    activeServiceDispatch({
      type: "UPDATE_CALC_VALUES",
      payload: updatedServiceData,
      versionInfo: { version: 0, comparisonVersion: 0, status: "OPEN" },
    });
  };

  useEffect(() => {
    const isActive = descriptionFocus === serviceData._id;
    if (isActive && inputRef.current) {
      // focus the newly mounted textarea
      inputRef.current.focus({ preventScroll: true });
      // optional: move caret to end
      const el = inputRef.current;
      const len = el.value?.length ?? 0;
      try {
        el.setSelectionRange(len, len);
      } catch {}
    }
  }, [descriptionFocus, serviceData._id]);

  useEffect(() => {
    const isActive = serviceFocus === serviceData._id;
    if (isActive && serviceInputRef.current) {
      // focus the newly mounted textarea
      serviceInputRef.current.focus({ preventScroll: true });
      // optional: move caret to end
      const el = serviceInputRef.current;
      const len = el.value?.length ?? 0;
      try {
        el.setSelectionRange(len, len);
      } catch {}
    }
  }, [serviceFocus, serviceData._id]);

  useEffect(() => {
    if (checkInDate) {
      setCheckInText(formatBRDate(checkInDate));
    }
  }, [checkInDate]);

  useEffect(() => {
    if (checkOutDate) {
      setCheckOutText(formatBRDate(checkOutDate));
    }
  }, [checkOutDate]);

  useEffect(() => {
    if (serviceValues.outDate && !checkOutDate) {
      setCheckOutDate(new Date(serviceValues.outDate));
    }
  }, [serviceValues.outDate]);

  useEffect(() => {
    if (serviceValues.inputDate && !checkInDate) {
      setCheckInDate(new Date(serviceValues.inputDate));
    }
  }, [serviceValues.inputDate]);

  useEffect(() => {
    if (checkInDate && checkOutDate) verifyDates(checkInDate, checkOutDate);
  }, [checkInDate, checkOutDate]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        // padding: "0px 8px",
        // border: "1px solid #eee",
        borderRadius: "4px",
        backgroundColor: "white",
      }}
    >
      <DescriptionModal
        isOpen={descriptionModal}
        onClose={() => setDescriptionModal(false)}
        descriptions={serviceValues.descriptions}
        onChange={handleDescriptionChange}
        onConfirm={submitForm}
      />
      <ApplicationPlaceModal
        isOpen={applicationPlaceModal}
        serviceValues={serviceValues}
        compareServiceValues={compareServiceValues}
        onClose={() => setApplicationPlaceModal(false)}
        onChange={handleStateChange}
        onConfirm={submitForm}
      />
      <TaxesModal
        isOpen={taxesModal}
        serviceValues={serviceValues}
        onClose={() => setTaxesModal(false)}
        onChange={handleStateChange}
        onConfirm={submitForm}
        mainAppliedTaxes={mainAppliedTaxes}
        exchangeCurrency={currency}
        categoryExchange={categoryExchange}
        eventScope={event?.eventScope}
      />
      {/* <span style={{ fontWeight: "bold" }}>Serviço {index + 1}</span> */}
      <Box
        display={"flex"}
        gap={3}
        alignItems={"center"}
        // mt={1}
        flexDirection={"row"}
      >
        <Box
          width={"100%"}
          flexDirection={"row"}
          display={"flex"}
          alignItems={"flex-start"}
          gap={2}
          flex={1}
        >
          <Box display={"flex"} flexDirection={"row"} flex={1}>
            {isFreeService ? (
              <LabeledInput
                label="Serviço"
                placeholder="Especifique"
                multiline
                disablePaste={false}
                disabled={false}
                maxRows={serviceFocus === serviceData?._id ? 25 : 1}
                value={serviceValues?.serviceType}
                helperText={compareServiceValues?.serviceType}
                onChange={(e) => handleInputBlur(e.target.value, "serviceType")}
                onFocus={(e) => {
                  setServiceFocus(serviceData?._id);
                }}
                onBlur={(e) => {
                  handleInputBlur(e.target.value, "serviceType");
                  setServiceFocus(null);
                }}
                inputRef={serviceInputRef}
                endAdornment={
                  serviceHasOverflow ? (
                    isInputServiceExpanded ? (
                      <MdExpandLess
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          (
                            serviceInputRef.current as HTMLTextAreaElement | null
                          )?.blur();
                          setServiceFocus(null);
                        }}
                      />
                    ) : (
                      <MdExpandMore
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => setServiceFocus(serviceData?._id)}
                      />
                    )
                  ) : null
                }
              />
            ) : (
              <Box
                display={"flex"}
                flexDirection={"column"}
                width={"100%"}
                marginTop={"5px"}
              >
                <Box>
                  <Typography variant="caption" style={{ marginBottom: "3px" }}>
                    Serviço
                  </Typography>
                </Box>
                <Select
                  size="small"
                  onChange={(e) => {
                    handleInputBlur(e.target.value, "serviceType");
                    // handleStateChange(e.target.value, "serviceType");
                  }}
                  value={serviceValues?.serviceType || "none"}
                  fullWidth
                  defaultValue="none"
                  disabled={false}
                >
                  <MenuItem value="none" disabled>
                    Clique para selecionar
                  </MenuItem>
                  {ServiceOptions[type].map((el: any) => {
                    return (
                      <MenuItem value={el.value} key={el.value}>
                        {el.text}
                      </MenuItem>
                    );
                  })}
                </Select>
                <FormHelperText>
                  {
                    ServiceOptions[type].find(
                      (el: any) =>
                        el.value === compareServiceValues?.serviceType
                    )?.text
                  }
                </FormHelperText>
              </Box>
            )}
          </Box>

          <Box display={"flex"} flex={1} sx={{ minWidth: 0 }}>
            <LabeledInputFull
              label="Descrição do serviço"
              disablePaste={false}
              placeholder="Especifique"
              multiline
              disabled={false}
              maxRows={descriptionFocus === serviceData?._id ? 25 : 1}
              value={serviceValues?.observation}
              helperText={compareServiceValues?.observation}
              onChange={(e) => handleStateChange(e.target.value, "observation")}
              onFocus={(e) => {
                setDescriptionFocus(serviceData?._id);
              }}
              onBlur={(e) => {
                setDescriptionFocus(null);
                handleInputBlur(e.target.value, "observation");
              }}
              inputRef={inputRef}
              endAdornment={
                hasOverflow ? (
                  isInputExpanded ? (
                    <MdExpandLess
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        (
                          inputRef.current as HTMLTextAreaElement | null
                        )?.blur();
                        setDescriptionFocus(null);
                      }}
                    />
                  ) : (
                    <MdExpandMore
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => setDescriptionFocus(serviceData?._id)}
                    />
                  )
                ) : null
              }
            />
          </Box>

          <Box display={"flex"} flex={0.3}>
            <LabeledInput
              value={serviceValues?.quantity}
              // helperText={String(compareServiceValues?.quantity)}
              onChange={(e) => {
                handleFieldChange(Number(e.target.value), "quantity");
              }}
              label={`Qtd`}
              disabled={false}
            />
          </Box>

          <Box display={"flex"} flex={0.3}>
            <LabeledInput
              // disabled
              onChange={(e) => {
                handleFieldChange(Number(e.target.value), "daily");
              }}
              onBlur={(e) => {
                const next = e.relatedTarget as Node | null;

                if (next && dailyPopperPaperRef.current?.contains(next)) return;

                setOpenCheckInCalendar(false);
                setOpenCheckOutCalendar(false);
                setOpenDailyMenu(false);
                setDailyAnchorEl(null);
                setCheckInAnchorEl(null);
                setCheckOutAnchorEl(null);
              }}
              onFocus={(e: any) => {
                const el = (e?.currentTarget as HTMLElement) || null;
                setDailyAnchorEl(el);
                setOpenDailyMenu(true);
              }}
              value={serviceValues?.daily} // fix later
              // helperText={String(compareServiceValues?.daily)}
              label={`Diárias`}
              disabled={false}
              onKeyDown={(e: React.KeyboardEvent) => {
                if (e.key === "Escape") {
                  e.preventDefault();
                  e.stopPropagation();

                  setOpenCheckInCalendar(false);
                  setOpenCheckOutCalendar(false);
                  setOpenDailyMenu(false);

                  setDailyAnchorEl(null);
                  setCheckInAnchorEl(null);
                  setCheckOutAnchorEl(null);

                  (e.currentTarget as HTMLElement)?.blur?.();
                }
              }}
            />

            <Popper
              open={openDailyMenu && !!dailyAnchorEl}
              anchorEl={dailyAnchorEl}
              placement="bottom-start"
              style={{ zIndex: 1500 }}
              modifiers={[
                { name: "offset", options: { offset: [0, 8] } },
                { name: "preventOverflow", options: { padding: 8 } },
              ]}
            >
              <ClickAwayListener
                onClickAway={(event) => {
                  const target = event.target as Node;
                  if (
                    dailyAnchorEl?.contains(target) ||
                    checkInAnchorEl?.contains(target) ||
                    checkOutAnchorEl?.contains(target)
                  )
                    return;
                  setOpenCheckInCalendar(false);
                  setOpenCheckOutCalendar(false);
                  setOpenDailyMenu(false);
                  setDailyAnchorEl(null);
                  setCheckInAnchorEl(null);
                  setCheckOutAnchorEl(null);
                }}
              >
                <Paper
                  ref={dailyPopperPaperRef}
                  elevation={3}
                  sx={{
                    p: 1.5,
                    minWidth: 260,
                    borderRadius: 1,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ display: "block", mb: 1, fontSize: "16px" }}
                  >
                    Data de entrada (check-in)
                  </Typography>

                  <TextField
                    size="small"
                    fullWidth
                    placeholder="dd/mm/aaaa"
                    value={checkInText}
                    onChange={(e) => {
                      const masked = applyDateMask(e.target.value);
                      setCheckInText(masked);
                      const parsed = parseBRDate(masked);
                      if (parsed) {
                        setCheckInDate(parsed);
                        setOpenCheckInCalendar(false);
                      }
                    }}
                    onFocus={(e) => {
                      setCheckInAnchorEl(e.currentTarget);
                      setOpenCheckInCalendar(true);
                      setOpenCheckOutCalendar(false);
                    }}
                    onBlur={() => {
                      const parsed = parseBRDate(checkInText);
                      if (parsed && canSetCheckIn(parsed, checkOutDate)) {
                        setCheckInDate(parsed);
                        handleInputBlur(parsed, "inputDate");
                      }
                    }}
                    onKeyDown={(e: React.KeyboardEvent) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        const parsed = parseBRDate(checkInText);
                        if (parsed && canSetCheckIn(parsed, checkOutDate)) {
                          setCheckInDate(parsed);
                          setOpenCheckInCalendar(false);
                          handleInputBlur(parsed, "inputDate");
                        } else if (!parsed && checkInText.length > 0) {
                          toast.warn(
                            "Data inválida. Use o formato dd/mm/aaaa."
                          );
                        }
                      }
                      if (e.key === "Escape") {
                        e.preventDefault();
                        e.stopPropagation();

                        setOpenCheckInCalendar(false);
                        setOpenCheckOutCalendar(false);
                        setOpenDailyMenu(false);

                        setDailyAnchorEl(null);
                        setCheckInAnchorEl(null);
                        setCheckOutAnchorEl(null);

                        (e.currentTarget as HTMLElement)?.blur?.();
                      }
                    }}
                    inputProps={{ maxLength: 10 }}
                  />

                  <Popper
                    open={openCheckInCalendar && !!checkInAnchorEl}
                    anchorEl={checkInAnchorEl}
                    placement="bottom-start"
                    style={{ zIndex: 1600 }}
                    modifiers={[
                      { name: "offset", options: { offset: [0, 8] } },
                      { name: "preventOverflow", options: { padding: 8 } },
                    ]}
                  >
                    <Paper elevation={4} sx={{ p: 1, borderRadius: 1 }}>
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DateCalendar
                          value={checkInDate}
                          onChange={(newValue) => {
                            const next = (newValue as Date | null) ?? null;
                            if (next && !canSetCheckIn(next, checkOutDate)) {
                              setOpenCheckInCalendar(false);
                              return;
                            }
                            setCheckInDate(next);
                            setOpenCheckInCalendar(false);
                            handleInputBlur(newValue, "inputDate");
                          }}
                        />
                      </LocalizationProvider>
                    </Paper>
                  </Popper>

                  <Box mt={1.5}>
                    <Typography
                      variant="caption"
                      sx={{ display: "block", mb: 1, fontSize: "16px" }}
                    >
                      Data de saída (check-out)
                    </Typography>

                    <TextField
                      size="small"
                      fullWidth
                      placeholder="dd/mm/aaaa"
                      value={checkOutText}
                      onChange={(e) => {
                        const masked = applyDateMask(e.target.value);
                        setCheckOutText(masked);
                        const parsed = parseBRDate(masked);
                        if (parsed) {
                          setCheckOutDate(parsed);
                          setOpenCheckOutCalendar(false);
                        }
                      }}
                      onFocus={(e) => {
                        setCheckOutAnchorEl(e.currentTarget);
                        setOpenCheckOutCalendar(true);
                        setOpenCheckInCalendar(false);
                      }}
                      onBlur={() => {
                        const parsed = parseBRDate(checkOutText);
                        if (parsed && canSetCheckOut(parsed, checkInDate)) {
                          setCheckOutDate(parsed);
                          handleInputBlur(parsed, "outDate");
                        }
                      }}
                      onKeyDown={(e: React.KeyboardEvent) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          const parsed = parseBRDate(checkOutText);
                          if (parsed && canSetCheckOut(parsed, checkInDate)) {
                            setCheckOutDate(parsed);
                            setOpenCheckOutCalendar(false);
                            handleInputBlur(parsed, "outDate");
                          } else if (!parsed && checkOutText.length > 0) {
                            toast.warn(
                              "Data inválida. Use o formato dd/mm/aaaa."
                            );
                          }
                        }
                        if (e.key === "Escape") {
                          e.preventDefault();
                          e.stopPropagation();

                          setOpenCheckInCalendar(false);
                          setOpenCheckOutCalendar(false);
                          setOpenDailyMenu(false);

                          setDailyAnchorEl(null);
                          setCheckInAnchorEl(null);
                          setCheckOutAnchorEl(null);

                          (e.currentTarget as HTMLElement)?.blur?.();
                        }
                      }}
                    />

                    <Popper
                      open={openCheckOutCalendar && !!checkOutAnchorEl}
                      anchorEl={checkOutAnchorEl}
                      placement="bottom-start"
                      style={{ zIndex: 1600 }}
                      modifiers={[
                        { name: "offset", options: { offset: [0, 8] } },
                        { name: "preventOverflow", options: { padding: 8 } },
                      ]}
                    >
                      <Paper elevation={4} sx={{ p: 1, borderRadius: 1 }}>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                          <DateCalendar
                            value={checkOutDate}
                            onChange={(newValue) => {
                              const next = (newValue as Date | null) ?? null;
                              if (next && !canSetCheckOut(next, checkInDate)) {
                                setOpenCheckOutCalendar(false);
                                return;
                              }
                              setCheckOutDate(next);
                              setOpenCheckOutCalendar(false);
                              handleInputBlur(newValue, "outDate");
                            }}
                            shouldDisableDate={(date) => {
                              if (!checkInDate) return false;
                              return (
                                startOfDayTs(date) < startOfDayTs(checkInDate)
                              );
                            }}
                            defaultCalendarMonth={checkInDate || undefined}
                          />
                        </LocalizationProvider>
                      </Paper>
                    </Popper>
                  </Box>
                </Paper>
              </ClickAwayListener>
            </Popper>
          </Box>

          <Box
            display={"flex"}
            flex={1}
            flexDirection={"column"}
            sx={{ pointerEvents: false ? "none" : "auto" }}
          >
            {!serviceValues?.taxData?.tariff ||
            (!serviceValues?.taxData?.tariff?.typeTax &&
              !isCortesy &&
              !isIncluded &&
              !isAcClient) ||
            (!isCortesy && !isIncluded && !isAcClient) ? (
              <Box mt={"8px"}>
                <UnitValueInput
                  key={`${serviceData._id}-${tabActive}-${serviceValuesKey}`}
                  label="Valor unitário"
                  prefix={currency}
                  value={serviceValues.taxData.tariff.value}
                  onChange={(value: number) => {
                    handleFieldChange(value, "taxData");
                  }}
                  helperText={
                    compareServiceValues?.taxData
                      ? formatUnitValue(
                          compareServiceValues.taxData.tariff.value,
                          currency
                        )
                      : undefined
                  }
                  InputProps={{
                    endAdornment: (
                      <Box sx={{ display: "flex", flexDirection: "row" }}>
                        <Tooltip
                          title={
                            <span style={{ fontSize: "16px" }}>Cortesia</span>
                          }
                          placement="top"
                          disableInteractive
                          PopperProps={{
                            modifiers: [
                              {
                                name: "offset",
                                options: {
                                  offset: [0, 2],
                                },
                              },
                            ],
                          }}
                        >
                          <Box
                            sx={{
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              ml: 0.5,
                              mt: -0.5,
                              p: 0.5,
                            }}
                            onClick={() =>
                              handleUpdateUnitValue(
                                "typeTax",
                                isCortesy
                                  ? typeTaxEnum.FIX_VALUE
                                  : typeTaxEnum.CORTESY
                              )
                            }
                          >
                            <MdCardGiftcard
                              style={{
                                color: isCortesy ? "#1976D2" : "black",
                                pointerEvents: "none",
                              }}
                            />
                          </Box>
                        </Tooltip>

                        <Tooltip
                          title={
                            <span style={{ fontSize: "16px" }}>Incluso</span>
                          }
                          placement="top"
                          disableInteractive
                          PopperProps={{
                            modifiers: [
                              {
                                name: "offset",
                                options: {
                                  offset: [0, 2],
                                },
                              },
                            ],
                          }}
                        >
                          <Box
                            sx={{
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              ml: 0.5,
                              mt: -0.5,
                              p: 0.5,
                            }}
                            onClick={() =>
                              handleUpdateUnitValue(
                                "typeTax",
                                isIncluded
                                  ? typeTaxEnum.FIX_VALUE
                                  : typeTaxEnum.INCLUDED
                              )
                            }
                          >
                            <FiCheckSquare
                              style={{
                                color: isCortesy ? "#1976D2" : "black",
                                pointerEvents: "none",
                              }}
                            />
                          </Box>
                        </Tooltip>

                        <Tooltip
                          title={
                            <span style={{ fontSize: "16px" }}>
                              A/C Cliente
                            </span>
                          }
                          placement="top"
                          disableInteractive
                          PopperProps={{
                            modifiers: [
                              {
                                name: "offset",
                                options: {
                                  offset: [0, 2],
                                },
                              },
                            ],
                          }}
                        >
                          <Box
                            sx={{
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              ml: 0.5,
                              mt: -0.5,
                              p: 0.5,
                            }}
                            onClick={() =>
                              handleUpdateUnitValue(
                                "typeTax",
                                isAcClient
                                  ? typeTaxEnum.DAILY
                                  : typeTaxEnum.AC_CLIENT
                              )
                            }
                          >
                            <FiUserCheck
                              style={{
                                color: isAcClient ? "#1976D2" : "black",
                                pointerEvents: "none",
                              }}
                            />
                          </Box>
                        </Tooltip>
                      </Box>
                    ),
                  }}
                  disabled={isCortesy || isIncluded || isAcClient}
                />
              </Box>
            ) : (
              <Box
                display={"flex"}
                flex={1}
                flexDirection={"column"}
                mt={"-1px"}
              >
                <LabeledInput
                  value={
                    isCortesy
                      ? "Cortesia"
                      : isIncluded
                      ? "Incluso"
                      : "A/C Cliente"
                  }
                  helperText={compareServiceValues?.daily}
                  label={`Valor unitário`}
                  disabled={isCortesy || isIncluded || isAcClient}
                  endAdornment={
                    isCortesy ? (
                      <MdCardGiftcard
                        style={{
                          cursor: "pointer",
                          color: isCortesy ? "#1976D2" : "black",
                          marginLeft: 8,
                          fontSize: 17,
                        }}
                        onClick={() =>
                          handleUpdateUnitValue(
                            "typeTax",
                            isCortesy
                              ? typeTaxEnum.FIX_VALUE
                              : typeTaxEnum.CORTESY
                          )
                        }
                      />
                    ) : isIncluded ? (
                      <FiCheckSquare
                        style={{
                          cursor: "pointer",
                          color: isIncluded ? "#1976D2" : "black",
                          marginLeft: 8,
                          fontSize: 17,
                        }}
                        onClick={() =>
                          handleUpdateUnitValue(
                            "typeTax",
                            isIncluded
                              ? typeTaxEnum.FIX_VALUE
                              : typeTaxEnum.INCLUDED
                          )
                        }
                      />
                    ) : (
                      <FiUserCheck
                        style={{
                          cursor: "pointer",
                          color: isAcClient ? "#1976D2" : "black",
                          marginLeft: 8,
                          fontSize: 17,
                        }}
                        onClick={() =>
                          handleUpdateUnitValue(
                            "typeTax",
                            isAcClient
                              ? typeTaxEnum.DAILY
                              : typeTaxEnum.AC_CLIENT
                          )
                        }
                      />
                    )
                  }
                />
              </Box>
            )}
          </Box>

          <Box display={"flex"} flex={0.5}>
            <LabeledInput
              disabled
              value={formatCurrencyValue(
                serviceValues?.taxData?.tariff.currencyValue || 0,
                currency
              )}
              label={`Valor total`}
              customHeight={40}
            />
          </Box>

          <Box
            display={"flex"}
            flex={0.2}
            alignItems={"center"}
            justifyContent={"center"}
            marginTop={4.5}
            gap={1}
          >
            {/* Indicador de taxas customizadas */}
            {(hasCustomTaxes(
              serviceValues?.appliedTaxes,
              mainAppliedTaxes,
              serviceValues?.appliedValues?.markupOverride,
              serviceValues?.appliedValues?.commissionOverride,
              Boolean(serviceValues?.appliedValues?.supplierTributeOverride) &&
                !serviceValues?.appliedValues?.markupOverride,
              { currency, eventScope: event?.eventScope }
            ) ||
              serviceValues?.exchangeOverride) && (
              <Tooltip
                title="Este serviço possui taxas ou câmbio diferentes do padrão"
                placement="top"
              >
                <Box
                  sx={{
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                  }}
                  onClick={() => setTaxesModal(true)}
                >
                  <MdWarning color="#FF9800" size={20} />
                </Box>
              </Tooltip>
            )}
            <Box
              sx={{
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
              }}
              onClick={(e) => {
                setAnchorEl(null);
                setOpenServiceMenu(false);
                handleDeleteService();
              }}
            >
              <IoIosCloseCircle color={"#D32F2F"} size={20} />
            </Box>
            <Box
              sx={{
                cursor: "default",
                display: "flex",
                alignItems: "center",
              }}
              onClick={(e) => {
                setAnchorEl(e.currentTarget);
                setOpenServiceMenu(true);
              }}
            >
              <BsThreeDotsVertical />
            </Box>
          </Box>
        </Box>
      </Box>

      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={openServiceMenu}
        onClose={() => {
          setAnchorEl(null);
          setOpenServiceMenu(false);
        }}
        MenuListProps={{
          "aria-labelledby": "basic-button",
        }}
        autoFocus={false}
      >
        {[
          "ATTRACTION",
          "BRINDES_INCENTIVOS_PREMIACOES",
          "CENOGRAPHY_DECORATION",
          "EQUIPMENTS",
        ].includes(type) && (
          <MenuItem
            autoFocus={false}
            onClick={() => {
              setAnchorEl(null);
              setOpenServiceMenu(false);
              setApplicationPlaceModal(true);
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <ApplicationPlaceIcon width={16} height={16} />
              <span>Local de aplicação</span>
            </Box>
          </MenuItem>
        )}
        <MenuItem
          autoFocus={false}
          onClick={() => {
            setAnchorEl(null);
            setOpenServiceMenu(false);
            setDescriptionModal(true);
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <DescriptionIcon width={16} height={16} />
            <span>Descritivos</span>
          </Box>
        </MenuItem>
        <MenuItem
          autoFocus={false}
          onClick={() => {
            setAnchorEl(null);
            setOpenServiceMenu(false);
            setTaxesModal(true);
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <PercentIcon width={16} height={16} />
            <span>Taxas</span>
          </Box>
        </MenuItem>
      </Menu>
    </Box>
  );
};

// Função de comparação customizada
const areEqual = (prevProps: Props, nextProps: Props) => {
  // Compara props que são passadas diretamente (shallow comparison para elas)
  if (
    prevProps.type !== nextProps.type ||
    prevProps.index !== nextProps.index ||
    prevProps.title !== nextProps.title ||
    prevProps.currency !== nextProps.currency
  ) {
    return false; // As props são diferentes, precisa re-renderizar
  }

  // Compara apenas as propriedades relevantes do objeto `serviceData`
  // Evitando uma deep comparison completa se o objeto for grande.
  if (
    prevProps.serviceData._id !== nextProps.serviceData._id
    // Adicione aqui outras propriedades de serviceData que impactam a renderização
  ) {
    return false; // serviceData mudou de forma relevante, precisa re-renderizar
  }

  // Se chegou aqui, todas as props relevantes são iguais, então não precisa re-renderizar
  return true;
};

export default ServiceGroupItem;
